import React, { useCallback } from 'react'

import { useTranslation } from '@/features/locale/LocaleProvider'
import { AddLiquidityActionBar } from '@/ui/dex/add-liquidity-action-bar'
import { AddLiquidityFirstPoolBanner, AddLiquidityPricePanel } from '@/ui/dex/add-liquidity-price-panel'
import { LiquidityFlowSeparator } from '@/ui/dex/liquidity-flow-separator'
import { TokenAmountRow } from '@/ui/dex/token-amount-row'
import { LiquidityMobileTokenPickerSheet } from '@/mobile/token-picker/LiquidityMobileTokenPickerSheet'
import type { AddLiquidityControllerResult } from '../../../../features/liquidity/useAddLiquidityController'
import { useLiquidityTokenSearch } from '../../../../features/liquidity/useLiquidityTokenSearch'
import { MintField as Field } from '../../../../domain/liquidity'
import { useAppWeb3 } from '../../../../hooks'
import { ChainId } from '../../../../sdk'
import { AddLiquidityConfirmModal } from '../AddLiquidityConfirmModal'

export function AddLiquidityForm({ controller }: { controller: AddLiquidityControllerResult }) {
  const t = useTranslation()
  const tokenSearch = useLiquidityTokenSearch()
  const { chainId } = useAppWeb3()
  const {
    atMaxAmounts,
    currencies,
    formattedAmounts,
    handleCurrencyASelect,
    handleCurrencyBSelect,
    handleMaxA,
    handleMaxB,
    onFieldAInput,
    onFieldBInput,
  } = controller

  const onTokenPick = useCallback(
    (field: 'A' | 'B', currency: Parameters<typeof handleCurrencyASelect>[0]) => {
      if (field === 'A') handleCurrencyASelect(currency)
      else handleCurrencyBSelect(currency)
    },
    [handleCurrencyASelect, handleCurrencyBSelect],
  )

  return (
    <>
      <AddLiquidityConfirmModal controller={controller} />
      <LiquidityMobileTokenPickerSheet
        open={tokenSearch.open}
        onClose={tokenSearch.close}
        chainId={chainId ?? ChainId.INFINITE_CREATIVE}
        title={tokenSearch.activeField === 'A' ? t.common.selectTokenA : t.common.selectTokenB}
        excludeCurrency={
          tokenSearch.activeField === 'A'
            ? (currencies[Field.CURRENCY_B] ?? undefined)
            : (currencies[Field.CURRENCY_A] ?? undefined)
        }
        onSelect={(currency) => tokenSearch.selectCurrency(currency, onTokenPick)}
      />

      <div className="flex flex-col gap-5">
        <AddLiquidityFirstPoolBanner controller={controller} />
        <TokenAmountRow
          id="add-liquidity-input-tokena"
          label="Token A"
          value={formattedAmounts[Field.CURRENCY_A]}
          currency={currencies[Field.CURRENCY_A]}
          onAmountChange={onFieldAInput}
          onTokenSelect={() => tokenSearch.openForField('A')}
          onMax={handleMaxA}
          showMaxButton={!atMaxAmounts[Field.CURRENCY_A]}
        />
        <LiquidityFlowSeparator />
        <TokenAmountRow
          id="add-liquidity-input-tokenb"
          label="Token B"
          value={formattedAmounts[Field.CURRENCY_B]}
          currency={currencies[Field.CURRENCY_B]}
          onAmountChange={onFieldBInput}
          onTokenSelect={() => tokenSearch.openForField('B')}
          onMax={handleMaxB}
          showMaxButton={!atMaxAmounts[Field.CURRENCY_B]}
        />
        <AddLiquidityPricePanel controller={controller} />
        <AddLiquidityActionBar controller={controller} />
      </div>
    </>
  )
}
